import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, concatMap, map } from "rxjs/operators";
import { Observable, of } from "rxjs";
import { AccountService } from "../../../entry/account.service";
import { createPluginProviderInfo, IPluginDataCalendar, IPluginDataPairing, IPluginDataProvider, PluginCalendarInfo, PluginPairingRequestInfo, PluginProviderInfo, PluginResponse, PluginSpaceInfo, ProviderInfo } from "./plugin.data";
import { Logger } from "../../../lib/common/logger";
import { AppConfigService } from "../../../app.config";
import { IPluginInstance } from "app/API/v1/Plugin/api.plugin.data";
import { environment } from "environments/environment";

@Injectable()
export class WorkplacePluginService {
    private readonly TAG: string = 'pluginSvc';
    private readonly UPDATE_DURATION: number = 300000;

    _providerInfo: { [pluginName: string]: { data: ProviderInfo, iadeaCareAccessInfo: { accessKeyID: string, accessKeyRemark: string, isAccessKeyEnabled: boolean }, lastUpdateTime: Date } } = {};
    _calendarInfo: { [pluginName: string]: { data: PluginCalendarInfo, lastUpdateTime: Date } } = {};
    _pairingInfo: { [pluginName: string]: { data: PluginPairingRequestInfo[], lastUpdateTime: Date } } = {};
    _reservableModuleInfo: { [pluginName: string]: { data: { id: string, name: string, checkinBeforeMeetingMinute: number }[], lastUpdateTime: Date } } = {};
    _pluginInstance: IPluginInstance;

    constructor(private http: HttpClient, private accountSvc: AccountService) { }

    getPluginBaseUrl(pluginName: string): string {
        let url: string = '';
        
        switch (pluginName) {
            default:
            case 'svcnow':
                {
                    url = environment.pluginConfig?.svcnow?.serverUrl;
                }
                break;
            case 'ems':
                {
                    url = environment.pluginConfig?.ems?.serverUrl;
                }
                break;
        }

        if (!url) {
            throw 'No plugin url config applied.';
        }

        url = `${url}/workplace`;
        if (pluginName) {
            url = `${url}/${pluginName}`;
        }

        return url;
    }

    reset(pluginName: string): void {
        if (this._providerInfo[pluginName]) {
            delete this._providerInfo[pluginName];
        }
        if (this._calendarInfo[pluginName]) {
            delete this._calendarInfo[pluginName];
        }
        if (this._pairingInfo[pluginName]) {
            delete this._pairingInfo[pluginName];
        }
    }

    isPluginRegistered(pluginName: string): boolean {
        return this._providerInfo?.[pluginName]?.data?.isValid();
    }

    savePluginInstance(pInstance: IPluginInstance): void {
        this._pluginInstance = pInstance;
    }

    getPluginInstance(): IPluginInstance {
        return this._pluginInstance;
    }

    getCalendarProviderConfig(pluginName: string): Observable<{ isFault: boolean, errorMessage?: string, data?: ProviderInfo, iadeaCareAccessInfo?: { accessKeyID: string, accessKeyRemark: string, isAccessKeyEnabled: boolean } }> {
        const existedProviderInfo = this._providerInfo[pluginName];
        if (!existedProviderInfo || !existedProviderInfo.data?.isValid() || (Date.now() - existedProviderInfo.lastUpdateTime?.getTime() > this.UPDATE_DURATION)) {
            return this.queryPluginServer<IPluginDataProvider>(this.accountSvc.token, pluginName, 'provider', null, { method: 'GET', queryParams: [{ key: 'id', value: this.accountSvc.enterpriseID }] }).pipe(
                map((res: { isFault: boolean, error?: number | string, errorMessage?: string, data?: IPluginDataProvider }) => {
                    Logger.logInfo(this.TAG, 'getCalendarProviderConfig', 'query res: ', res);
                    if (res.isFault) {
                        return { isFault: true, error: res.error, errorMessage: res.errorMessage };
                    }

                    this._providerInfo[pluginName] = {
                        data: createPluginProviderInfo(res.data),
                        iadeaCareAccessInfo: {
                            accessKeyID: res.data.iadeaCareAccessInfo.accessKeyID,
                            accessKeyRemark: res.data.iadeaCareAccessInfo.accessKeyRemark,
                            isAccessKeyEnabled: res.data.iadeaCareAccessInfo.isAccessKeyEnabled
                        },
                        lastUpdateTime: new Date()
                    };

                    Logger.logInfo(this.TAG, 'getCalendarProviderConfig', 'provider config: ', this._providerInfo[pluginName]);

                    if (!this.isPluginRegistered(pluginName)) {
                        return { isFault: true, error: res.error || 'Unknown', errorMessage: res.errorMessage };
                    }

                    return { isFault: false, data: this._providerInfo[pluginName]?.data, iadeaCareAccessInfo: this._providerInfo[pluginName]?.iadeaCareAccessInfo };
                })
            );
        }

        return of({ isFault: false, data: this._providerInfo[pluginName]?.data, iadeaCareAccessInfo: this._providerInfo[pluginName]?.iadeaCareAccessInfo });
    }

    createCalendarProviderConfig(pluginName: string, data: Partial<IPluginDataProvider>): Observable<{ isFault: boolean, errorMessage?: string, data?: PluginProviderInfo, iadeaCareAccessInfo?: { accessKeyID: string, accessKeyRemark: string, isAccessKeyEnabled: boolean } }> {
        return this.queryPluginServer<Partial<IPluginDataProvider>>(this.accountSvc.token, pluginName, 'provider', data, { method: 'POST' }).pipe(
            map((res: { isFault: boolean, error?: number | string, errorMessage?: string, data?: Partial<IPluginDataProvider> }) => {
                Logger.logInfo(this.TAG, 'createCalendarProviderConfig', 'query res: ', res);
                if (res.isFault) {
                    return { isFault: true, error: res.error, errorMessage: res.errorMessage };
                }

                this._providerInfo[pluginName] = {
                    data: createPluginProviderInfo(res.data),
                    iadeaCareAccessInfo: {
                        accessKeyID: res.data.iadeaCareAccessInfo.accessKeyID,
                        accessKeyRemark: res.data.iadeaCareAccessInfo.accessKeyRemark,
                        isAccessKeyEnabled: res.data.iadeaCareAccessInfo.isAccessKeyEnabled
                    },
                    lastUpdateTime: new Date()
                };

                return { isFault: false, data: this._providerInfo[pluginName]?.data, iadeaCareAccessInfo: this._providerInfo[pluginName]?.iadeaCareAccessInfo };
            })
        );
    }

    removeCalendarProviderConfig(pluginName: string): Observable<{ isFault: boolean, error?: number | string, errorMessage?: string, data?: any }> {
        return this.queryPluginServer<void>(this.accountSvc.token, pluginName, 'provider', null, { method: 'DELETE', queryParams: [{ key: 'id', value: this.accountSvc.enterpriseID }] }).pipe(
            map((res: { isFault: boolean, error?: number | string, errorMessage?: string, data?: void }) => {
                if (!res.isFault) {
                    this.reset(pluginName);
                }

                return res;
            })
        );
    }

    updateCalendarProviderConfig(pluginName: string, data: Partial<IPluginDataProvider>): Observable<{ isFault: boolean, errorMessage?: string, data?: PluginProviderInfo, iadeaCareAccessInfo?: { accessKeyID: string, accessKeyRemark: string, isAccessKeyEnabled: boolean } }> {
        return this.queryPluginServer<Partial<IPluginDataProvider>>(this.accountSvc.token, pluginName, 'provider', data, { method: 'PUT' }).pipe(
            map((res: { isFault: boolean, error?: number | string, errorMessage?: string, data?: Partial<IPluginDataProvider> }) => {
                Logger.logInfo(this.TAG, 'updateCalendarProviderConfig', 'query res: ', res);
                if (res.isFault) {
                    return { isFault: true, error: res.error, errorMessage: res.errorMessage };
                }

                this._providerInfo[pluginName] = {
                    data: createPluginProviderInfo(res.data),
                    iadeaCareAccessInfo: {
                        accessKeyID: res.data.iadeaCareAccessInfo.accessKeyID,
                        accessKeyRemark: res.data.iadeaCareAccessInfo.accessKeyRemark,
                        isAccessKeyEnabled: res.data.iadeaCareAccessInfo.isAccessKeyEnabled
                    },
                    lastUpdateTime: new Date()
                };

                return { isFault: false, data: this._providerInfo[pluginName]?.data, iadeaCareAccessInfo: this._providerInfo[pluginName]?.iadeaCareAccessInfo };
            })
        );
    }

    getCalendarConfig(pluginName: string, force: boolean = false): Observable<{ isFault: boolean, errorMessage?: string, data?: PluginCalendarInfo }> {
        const existedCalendarInfo = this._calendarInfo[pluginName];
        if (force || !existedCalendarInfo || !existedCalendarInfo?.data || (Date.now() - existedCalendarInfo.lastUpdateTime?.getTime() > this.UPDATE_DURATION)) {
            return this.queryPluginServer<IPluginDataCalendar>(this.accountSvc.token, pluginName, 'calendar', null, { method: 'GET', queryParams: [{ key: 'id', value: this.accountSvc.enterpriseID }] }).pipe(
                map((res: { isFault: boolean, error?: number | string, errorMessage?: string, data?: IPluginDataCalendar }) => {
                    Logger.logInfo(this.TAG, 'getCalendarConfig', 'query res: ', res);
                    if (res.isFault) {
                        return { isFault: true, error: res.error, errorMessage: res.errorMessage };
                    }

                    this._calendarInfo[pluginName] = {
                        data: new PluginCalendarInfo(`${this.getPluginBaseUrl(pluginName)}/config/calendar/image`, this.accountSvc.enterpriseID, this.accountSvc.token, res.data),
                        lastUpdateTime: new Date()
                    };

                    return { isFault: false, data: this._calendarInfo[pluginName]?.data };
                })
            );
        }

        return of({ isFault: false, data: this._calendarInfo[pluginName]?.data });
    }

    updateCalendarConfig(pluginName: string, data: Partial<IPluginDataCalendar>): Observable<{ isFault: boolean, errorMessage?: string, data?: PluginCalendarInfo }> {
        return this.queryPluginServer<Partial<IPluginDataCalendar>>(this.accountSvc.token, pluginName, 'calendar', data, { method: 'POST' }).pipe(
            map((res: { isFault: boolean, error?: number | string, errorMessage?: string, data?: Partial<IPluginDataCalendar> }) => {
                Logger.logInfo(this.TAG, 'updateCalendarConfig', 'query res: ', res);
                if (res.isFault) {
                    return { isFault: true, error: res.error, errorMessage: res.errorMessage };
                }

                this._calendarInfo[pluginName] = {
                    data: new PluginCalendarInfo(`${this.getPluginBaseUrl(pluginName)}/config/calendar/image`, this.accountSvc.enterpriseID, this.accountSvc.token, res.data),
                    lastUpdateTime: new Date()
                };

                return { isFault: false, data: this._calendarInfo[pluginName]?.data };
            })
        );
    }

    updateCalendarImage(pluginName: string, files: { file: File, fieldname: string, filename: string }[]): any {
        const formData = new FormData();
        files.forEach(f => {
            formData.append(f.fieldname, f.file);
            formData.append('name', f.filename);
        });

        return this.queryPluginServer<FormData>(this.accountSvc.token, pluginName, 'calendar/image', formData, { method: 'POST', queryParams: [{ key: 'id', value: this.accountSvc.enterpriseID }] });
    }

    getWorkingHour(pluginName: string): Observable<{ isFault: boolean, error?: number | string, errorMessage?: string, data?: { dayStart: string, dayEnd: string, dayHalf: string } }> {
        return this.queryPluginServer<{ dayStart: string, dayEnd: string, dayHalf: string }>(this.accountSvc.token, pluginName, 'props', null, { method: 'GET', queryParams: [{ key: 'id', value: this.accountSvc.enterpriseID }, { key: 'propNames', value: 'sn_wsd_rsv.day_start,sn_wsd_rsv.day_end'}] }).pipe(
            map((res: { isFault: boolean, error?: number | string, errorMessage?: string, data?: { [propName: string]: any } }) => {
                Logger.logInfo(this.TAG, 'getWorkingHour', 'query res: ', res);
                if (res.isFault) {
                    return { isFault: true, error: res.error, errorMessage: res.errorMessage };
                }

                return { isFault: false, data: { dayStart: res.data?.['sn_wsd_rsv.day_start'], dayEnd: res.data?.['sn_wsd_rsv.day_end'], dayHalf: '13:00' } };
            })
        );
    }

    getReservableModules(pluginName: string, force: boolean = false): Observable<{ isFault: boolean, error?: number | string, errorMessage?: string, data?: { id: string, name: string, checkinBeforeMeetingMinute: number }[] }> {
        const existedReservableModuleInfo = this._reservableModuleInfo[pluginName];
        if (force || !existedReservableModuleInfo || (Date.now() - existedReservableModuleInfo.lastUpdateTime?.getTime() > this.UPDATE_DURATION)) {
            return this.queryPluginServer<{ id: string, name: string, checkinBeforeMeetingMinute: number }[]>(this.accountSvc.token, pluginName, 'reservableModules', null, { method: 'GET', queryParams: [{ key: 'id', value: this.accountSvc.enterpriseID }, { key: 'force', value: force }] }).pipe(
                map((res: { isFault: boolean, error?: number | string, errorMessage?: string, data?: { id: string, name: string, checkinBeforeMeetingMinute: number }[] }) => {
                    Logger.logInfo(this.TAG, 'getReservableModules', 'query res: ', res);
                    if (res.isFault) {
                        return { isFault: true, error: res.error, errorMessage: res.errorMessage };
                    }

                    this._reservableModuleInfo[pluginName] = {
                        data: res.data,
                        lastUpdateTime: new Date()
                    };

                    return { isFault: false, data: this._reservableModuleInfo[pluginName]?.data };
                })
            )
        }

        return of({ isFault: false, data: this._reservableModuleInfo[pluginName]?.data });
    }

    updateReservableModules(pluginName: string, datas: { spaceID: string, reservableModuleID: string, checkinBeforeMeetingMinute: number }[]): Observable<{ isFault: boolean, errorMessage?: string, data?: any }> {
        return this.queryPluginServer<any>(this.accountSvc.token, pluginName, 'reservableModules', datas, { method: 'POST' }).pipe(
            map((res: { isFault: boolean, error?: number | string, errorMessage?: string, data?: any }) => {
                Logger.logInfo(this.TAG, 'updateReservableModules', 'query res: ', res);
                if (res.isFault) {
                    return { isFault: true, error: res.error, errorMessage: res.errorMessage };
                }

                return { isFault: false, };
            })
        );
    }

    updateOfficeHours(pluginName: string, datas: { spaceID: string, officeHour: { dayStart: string, dayEnd: string, dayHalf: string } }[]): Observable<{ isFault: boolean, errorMessage?: string, data?: any }> {
        return this.queryPluginServer<any>(this.accountSvc.token, pluginName, 'officeHours', datas, { method: 'POST' }).pipe(
            map((res: { isFault: boolean, error?: number | string, errorMessage?: string, data?: any }) => {
                Logger.logInfo(this.TAG, 'updateOfficeHours', 'query res: ', res);
                if (res.isFault) {
                    return { isFault: true, error: res.error, errorMessage: res.errorMessage };
                }

                return { isFault: false, };
            })
        );
    }

    getPairingConfig(pluginName: string, force: boolean = false): Observable<{ isFault: boolean, error?: number | string, errorMessage?: string, data?: PluginPairingRequestInfo[] }> {
        const existedPairingInfo = this._pairingInfo[pluginName];
        if (force || !existedPairingInfo || (Date.now() - existedPairingInfo.lastUpdateTime?.getTime() > this.UPDATE_DURATION)) {
            return this.queryPluginServer<IPluginDataPairing[]>(this.accountSvc.token, pluginName, 'pair', null, { method: 'GET', queryParams: [{ key: 'id', value: this.accountSvc.enterpriseID }, { key: 'force', value: force }] }).pipe(
                map((res: { isFault: boolean, error?: number | string, errorMessage?: string, data?: Partial<IPluginDataPairing[]> }) => {
                    Logger.logInfo(this.TAG, 'getPairingConfig', 'query res: ', res);
                    if (res.isFault) {
                        return { isFault: true, error: res.error, errorMessage: res.errorMessage };
                    }

                    this._pairingInfo[pluginName] = {
                        data: res.data.map(d => Object.assign(new PluginSpaceInfo(d), { devices: d.devices })),
                        lastUpdateTime: new Date()
                    };

                    return { isFault: false, data: this._pairingInfo[pluginName]?.data };
                })
            );
        }

        return of({ isFault: false, data: this._pairingInfo[pluginName]?.data });
    }

    updatePairingConfig(pluginName: string, data: Partial<PluginPairingRequestInfo[]>): Observable<{ isFault: boolean, errorMessage?: string, data?: any }> {
        return this.queryPluginServer<Partial<PluginPairingRequestInfo[]>>(this.accountSvc.token, pluginName, 'pair', data, { method: 'POST' }).pipe(
            map((res: { isFault: boolean, error?: number | string, errorMessage?: string, data?: Partial<PluginPairingRequestInfo[]> }) => {
                Logger.logInfo(this.TAG, 'updatePairingConfig', 'query res: ', res);
                if (res.isFault) {
                    return { isFault: true, error: res.error, errorMessage: res.errorMessage };
                }

                this._pairingInfo[pluginName] = {
                    data: res.data.map(d => Object.assign(new PluginSpaceInfo(d), { devices: d.devices })),
                    lastUpdateTime: new Date()
                };

                return { isFault: false, data: this._pairingInfo[pluginName]?.data };
            })
        );
    }

    private queryPluginServer<T>(token: string, pluginName: string, subpath: string, data: any, options?: { method: string, queryParams?: { key: string, value: any }[] }): Observable<{ isFault: boolean, error?: number | string, errorMessage?: string, data?: T }> {
        //Logger.logInfo(this.TAG, 'queryPluginServer', 'input arguments: ', arguments);
        return of(`${this.getPluginBaseUrl(pluginName)}/config/${subpath}`).pipe(
            concatMap((url: string) => {
                let header: HttpHeaders = new HttpHeaders();
                header = header.set('Accept', 'application/json');
                header = header.set('Authorization', 'Bearer ' + token);
                header = header.set('X-API-URL', AppConfigService.configs.server.api.baseUrl);

                let param: HttpParams = new HttpParams();
                options?.queryParams?.forEach((p: { key: string, value: any }) => {
                    param = param.set(p.key, p.value);
                });

                switch (options?.method) {
                    case 'DELETE':
                        {
                            return this.http.delete(url, { headers: header, params: param }).pipe(
                                map((res: PluginResponse<T>) => {
                                    if (res.error !== 0) {
                                        throw res;
                                    }

                                    return { isFault: false, data: res.data };
                                })
                            );
                        }
                    case 'PUT':
                        {
                            let reqBody: any;
                            if (data instanceof FormData) {
                                reqBody = data;
                            }
                            else {
                                reqBody = {
                                    account: {
                                        enterpriseID: this.accountSvc.enterpriseID,
                                        enterpriseName: this.accountSvc.enterpriseName,
                                        userAccountID: this.accountSvc.accountID,
                                        userAccountName: this.accountSvc.accountName
                                    },
                                    data: data
                                };
                            }

                            return this.http.put(url, reqBody, { headers: header, params: param }).pipe(
                                map((res: PluginResponse<T>) => {
                                    if (res.error !== 0) {
                                        throw res;
                                    }

                                    return { isFault: false, data: res.data };
                                })
                            )
                        }
                    case 'POST':
                        {
                            let reqBody: any;
                            if (data instanceof FormData) {
                                reqBody = data;
                            }
                            else {
                                reqBody = {
                                    account: {
                                        enterpriseID: this.accountSvc.enterpriseID,
                                        enterpriseName: this.accountSvc.enterpriseName,
                                        userAccountID: this.accountSvc.accountID,
                                        userAccountName: this.accountSvc.accountName
                                    },
                                    data: data
                                };
                            }

                            return this.http.post(url, reqBody, { headers: header, params: param }).pipe(
                                map((res: PluginResponse<T>) => {
                                    if (res.error !== 0) {
                                        throw res;
                                    }

                                    return { isFault: false, data: res.data };
                                })
                            )
                        }
                    default:
                    case 'GET':
                        {
                            return this.http.get(url, { headers: header, params: param }).pipe(
                                map((res: PluginResponse<T>) => {
                                    if (res.error !== 0) {
                                        throw res;
                                    }

                                    return { isFault: false, data: res.data };
                                })
                            );
                        }
                }
            }),
            catchError((err) => {
                Logger.logError(this.TAG, 'queryPluginServer', 'exception: ', err);
                return of({ isFault: true, error: err.error, errorMessage: err.errorMessage || `[${err.status}] ${err.statusText}` })
            })
        );
    }
}